import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, MapPin, MessageSquare, Loader2, ArrowLeft, Calendar, Send } from 'lucide-react';
import { sanitizeStyleBlock } from '../utils/sanitizeHtml';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Inject the same marketing fonts so this page feels consistent with `/`.
  useEffect(() => {
    const id = 'ludy-marketing-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,400;0,500;0,700;1,400;1,700&family=Nunito+Sans:ital,opsz,wght@0,6..12,300;0,6..12,400;0,6..12,500;0,6..12,600;0,6..12,700;1,6..12,400&display=swap';
    document.head.appendChild(link);
  }, []);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || form.message.trim().length < 10) {
      toast.error('Please fill out name, email, subject, and a message of at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          subject: form.subject.trim(),
          message: form.message.trim(),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Could not send your message');
      setSubmitted(true);
      toast.success(body.message || "Thanks — we'll get back to you soon.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageStyle}>
      <style dangerouslySetInnerHTML={{ __html: sanitizeStyleBlock(styles) }} />

      <nav className="contact-nav">
        <button onClick={() => navigate('/')} className="contact-nav-back">
          <ArrowLeft size={16} /> Back to home
        </button>
        <img src="/ludy-wordmark.png" alt="Ludy" className="contact-nav-logo" />
        <Link to="/auth" className="contact-nav-cta">Sign in</Link>
      </nav>

      <main className="contact-main">
        <div className="contact-grid">
          {/* Left: intro + channels */}
          <aside className="contact-aside">
            <div className="contact-eyebrow">
              <span className="contact-dot" /> Contact
            </div>
            <h1 className="contact-title">
              Let's talk about <em>your sales</em>.
            </h1>
            <p className="contact-sub">
              Questions about Ludy, custom plans, or want a live walkthrough? Drop us a line — we usually reply within a few hours.
            </p>

            <div className="contact-channels">
              <a href="mailto:hello@ludy.com" className="channel-card">
                <div className="channel-icon"><Mail size={18} /></div>
                <div>
                  <p className="channel-title">Email us</p>
                  <p className="channel-value">hello@ludy.com</p>
                </div>
              </a>
              <a href="https://calendly.com/luka-wbm/30min" target="_blank" rel="noopener noreferrer" className="channel-card">
                <div className="channel-icon"><Calendar size={18} /></div>
                <div>
                  <p className="channel-title">Book a 30-min call</p>
                  <p className="channel-value">calendly.com/luka-wbm</p>
                </div>
              </a>
              <div className="channel-card no-link">
                <div className="channel-icon"><MapPin size={18} /></div>
                <div>
                  <p className="channel-title">Headquarters</p>
                  <p className="channel-value">Remote-first · Europe</p>
                </div>
              </div>
            </div>
          </aside>

          {/* Right: form */}
          <section className="contact-form-card">
            {submitted ? (
              <div className="contact-success" data-testid="contact-success">
                <div className="success-icon"><MessageSquare size={28} /></div>
                <h2>Message received.</h2>
                <p>Thanks, {form.name.split(' ')[0] || 'friend'}. We'll reply to <strong>{form.email}</strong> within 24 hours.</p>
                <button className="btn-primary-flat" onClick={() => navigate('/')}>
                  Back to home
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="form-row">
                  <div className="form-field">
                    <label>Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={handleChange('name')}
                      placeholder="Jane Doe"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="form-field">
                    <label>Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={handleChange('email')}
                      placeholder="jane@company.com"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Company <span className="optional">(optional)</span></label>
                    <input
                      type="text"
                      value={form.company}
                      onChange={handleChange('company')}
                      placeholder="Acme Inc."
                      data-testid="contact-company"
                    />
                  </div>
                  <div className="form-field">
                    <label>Subject</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={handleChange('subject')}
                      placeholder="Pricing question"
                      required
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Message</label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder="Tell us a bit about your team and what you're trying to solve..."
                    required
                    data-testid="contact-message"
                  />
                  <p className="form-hint">{form.message.length} / 5000 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary-flat"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="spinning" /> Sending...</>
                  ) : (
                    <><Send size={16} /> Send message</>
                  )}
                </button>
                <p className="form-foot">By submitting, you agree to our terms. We never share your information.</p>
              </form>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  background: '#FBFAF6',
  color: '#1A1A18',
  fontFamily: "'Nunito Sans', -apple-system, sans-serif",
};

const styles = `
  .contact-nav {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 40px; max-width: 1280px; margin: 0 auto;
  }
  .contact-nav-back {
    display: inline-flex; align-items: center; gap: 6px;
    background: transparent; border: 1px solid #e6e2d6; border-radius: 8px;
    padding: 8px 14px; font-size: 13px; color: #6B6B62; cursor: pointer;
    transition: color .2s, border-color .2s, background .2s;
    font-family: inherit;
  }
  .contact-nav-back:hover { color: #0E5944; border-color: #0E5944; background: rgba(14,89,68,0.04); }
  .contact-nav-logo { height: 36px; }
  .contact-nav-cta {
    color: #1A1A18; font-size: 14px; font-weight: 500; text-decoration: none;
    padding: 9px 18px; border-radius: 8px; background: #1A1A18; color: #fff;
  }

  .contact-main { max-width: 1180px; margin: 0 auto; padding: 40px 40px 80px; }
  .contact-grid {
    display: grid; grid-template-columns: 1fr 1.2fr; gap: 64px;
    align-items: start;
  }
  @media (max-width: 900px) {
    .contact-nav { padding: 16px 20px; }
    .contact-main { padding: 24px 20px 60px; }
    .contact-grid { grid-template-columns: 1fr; gap: 40px; }
  }

  .contact-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: #FAF1D9; border: 1px solid #E8C97A; border-radius: 100px;
    padding: 6px 14px; font-size: 11px; font-weight: 500; color: #8A6516;
    letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 24px;
  }
  .contact-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #C9A84C;
    animation: contact-pulse 2s infinite;
  }
  @keyframes contact-pulse {
    0%,100% { opacity: 1; transform: scale(1); }
    50% { opacity: .5; transform: scale(.7); }
  }
  .contact-title {
    font-family: 'Ubuntu', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 56px; font-weight: 700; line-height: 1.05;
    letter-spacing: -1.5px; color: #1A1A18; margin: 0 0 20px;
  }
  .contact-title em {
    font-style: italic; color: #0E5944;
    font-family: 'Ubuntu', -apple-system, BlinkMacSystemFont, sans-serif; font-weight: 500;
  }
  @media (max-width: 700px) { .contact-title { font-size: 40px; } }
  .contact-sub {
    font-size: 16px; line-height: 1.6; color: #6B6B62; max-width: 480px; margin: 0 0 32px;
  }

  .contact-channels { display: flex; flex-direction: column; gap: 12px; }
  .channel-card {
    display: flex; align-items: center; gap: 14px;
    background: #fff; border: 1px solid #e6e2d6; border-radius: 12px;
    padding: 14px 16px; text-decoration: none; color: inherit;
    transition: border-color .2s, transform .2s, background .2s;
  }
  .channel-card:hover:not(.no-link) {
    border-color: #0E5944; transform: translateY(-1px);
    background: rgba(14,89,68,0.02);
  }
  .channel-icon {
    width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
    background: rgba(14,89,68,0.08); color: #0E5944;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .channel-title { font-size: 13px; color: #6B6B62; margin: 0 0 2px; }
  .channel-value { font-size: 14px; font-weight: 500; color: #1A1A18; margin: 0; }

  .contact-form-card {
    background: #fff; border: 1px solid #e6e2d6; border-radius: 16px;
    padding: 36px; box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  }
  @media (max-width: 700px) { .contact-form-card { padding: 24px; } }

  .form-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px;
  }
  @media (max-width: 600px) { .form-row { grid-template-columns: 1fr; } }
  .form-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
  .form-field label {
    font-size: 12px; font-weight: 600; color: #1A1A18;
    letter-spacing: 0.02em; text-transform: uppercase;
  }
  .form-field .optional { color: #9ca3af; font-weight: 400; text-transform: none; letter-spacing: 0; }
  .form-field input, .form-field textarea {
    width: 100%; box-sizing: border-box;
    padding: 11px 14px; border: 1px solid #e6e2d6; border-radius: 10px;
    background: #FBFAF6; color: #1A1A18; font-size: 14px;
    font-family: inherit;
    transition: border-color .15s, background .15s, box-shadow .15s;
  }
  .form-field input:focus, .form-field textarea:focus {
    outline: none; border-color: #0E5944; background: #fff;
    box-shadow: 0 0 0 3px rgba(14,89,68,0.08);
  }
  .form-field textarea { resize: vertical; min-height: 130px; line-height: 1.55; }
  .form-hint { font-size: 11px; color: #9ca3af; margin: 4px 0 0; align-self: flex-end; }

  .btn-primary-flat {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    background: #0E5944; color: #fff; border: none; border-radius: 10px;
    padding: 13px 26px; font-size: 14px; font-weight: 500; cursor: pointer;
    font-family: inherit; transition: background .2s, transform .2s;
    margin-top: 8px;
  }
  .btn-primary-flat:hover:not(:disabled) { background: #0a4434; transform: translateY(-1px); }
  .btn-primary-flat:disabled { opacity: 0.6; cursor: not-allowed; }

  .form-foot { font-size: 12px; color: #9ca3af; margin: 16px 0 0; text-align: center; }

  .contact-success { text-align: center; padding: 24px 8px; }
  .success-icon {
    width: 64px; height: 64px; border-radius: 50%;
    background: rgba(14,89,68,0.10); color: #0E5944;
    display: inline-flex; align-items: center; justify-content: center;
    margin-bottom: 18px;
  }
  .contact-success h2 {
    font-family: 'Ubuntu', -apple-system, BlinkMacSystemFont, sans-serif; font-size: 28px; letter-spacing: -.6px;
    color: #1A1A18; margin: 0 0 10px;
  }
  .contact-success p { color: #6B6B62; font-size: 15px; margin: 0 0 24px; }

  .spinning { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;
